import { IconName } from 'src/components';
import { Space } from 'src/utils/theme';
import { WebSessionType } from 'src/api/learningSessions/types';

export type SessionColor = 'green' | 'red' | 'blue';

interface SessionTypeVariables {
  sessionIcon: IconName;
  sessionColor: SessionColor;
  width: Space;
  slug: string;
  isPro: boolean;
}

/**
 * For UX reasons, we distinguish between three categories of session: learn, review and speed
 * Each category has it's own color which we express in this object
 */
const colorMap: Record<'learn' | 'review' | 'speed', SessionColor> = {
  learn: 'green',
  review: 'blue',
  speed: 'red',
};

type SessionConfig = Record<WebSessionType, SessionTypeVariables>;
const sessionConfig: SessionConfig = {
  learn: {
    sessionIcon: 'learnFilled',
    sessionColor: colorMap.learn,
    width: 'xl',
    slug: 'learn',
    isPro: false,
  },
  speed_review: {
    sessionIcon: 'speedReviewFilled',
    sessionColor: colorMap.speed,
    width: 'lg',
    slug: 'speed',
    isPro: false,
  },
  video: {
    sessionIcon: 'learnFilled',
    sessionColor: colorMap.review,
    width: 'xl',
    slug: 'video',
    isPro: true,
  },
  classic_review: {
    sessionIcon: 'practice',
    sessionColor: colorMap.review,
    width: 'xl',
    slug: 'review',
    isPro: false,
  },
  review: {
    sessionIcon: 'practice',
    sessionColor: colorMap.review,
    width: 'xl',
    slug: 'review',
    isPro: false,
  },
  listening_skills: {
    sessionIcon: 'soundFull',
    sessionColor: colorMap.review,
    width: 'xl',
    slug: 'audio',
    isPro: true,
  },
  difficult_words: {
    sessionIcon: 'difficultWordsFilled',
    sessionColor: colorMap.review,
    width: 'xl',
    slug: 'difficult',
    isPro: true,
  },
  mixed: {
    sessionIcon: 'practice',
    sessionColor: colorMap.review,
    width: 'xl',
    slug: 'mixed',
    isPro: false,
  },
};

/**
 * Returns `null` when it cannot find a session type with that slug
 * (e.g. when the user passes an inappropriate value to the URL)
 */
export const slugToSessionType = (slug: string): WebSessionType | null => {
  const matchingSlug = Object.entries(sessionConfig).filter(
    ([, variables]) => variables.slug === slug,
  );
  if (matchingSlug.length === 0) return null;
  const [sessionTypeVarsPair] = matchingSlug;
  const [sessionType] = sessionTypeVarsPair;
  return sessionType as WebSessionType;
};

export const getSessionTypeVars = (sessionType: WebSessionType) => sessionConfig[sessionType];
